// src/services/assessment/inex.ts
import { Assessment, AssessmentInfoRequest, AssessmentSubmission, AssessmentSubmissions, AssessmentTriggerDemoNotifactionRequest, GenerateInterviewSectionRequest, GetDemoCompanyFilterParams, GetMyAssessmentParams, GetSubmissionJobAssessmentParams, InterviewAssessment, UserForm, UserInterview } from '@type/assessments';
import axios, { AxiosResponse, AxiosError } from 'axios';
import { getCookie } from '@util/cookie';
import { fatalErrorLog } from '@util/sentry';
import { DemoInterviewFormRequest } from '@type/interview';

export interface AssessmentResponse {
  code: number;
  message: string;
  data: {
    id: string;
    type: "Form" | "Interview";
    hr_id: string;
    content: {
      title: string;
      description: string;
      type?: "video" | "voice"; // Only for Interview assessments
      questions?: {
        type: "Multiple Choice" | "Short Answer" | "Rating Scale" | "Prompt";
        content: string;
        id: string;
        options?: string[]; // Only for Multiple Choice questions
        correct_answers?: string[]; // Only for Short Answer questions
      }[];
    }

    status: "Draft" | "Active" | "Archived";
    created_at: string;
    updated_at: string;
  }
  errors?: string[];
}

// Enhanced response interface for API responses
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  status?: number;
  message?: string;
  error?: any;
}

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

export const getAssessments = async (): Promise<Assessment[]> => {
  try {
    const authToken = getCookie("authToken");

    if (!authToken) {
      return [] as Assessment[]; // Return an empty array if there's no auth token
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/assessments/hr/my_assessments`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`
      },
      timeout: 10000,
    });

    return response.data.data;

  } catch (error) {
    const err = error as AxiosError;

    return [] as Assessment[]; // Return an empty array if there's an error
  }
};

export const getListAssessment = async (): Promise<any> => {
  const authToken = getCookie("authToken");
  return await axios({
    url: `${apiURL}/assessments/hr/my_assessments`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${authToken}`
    },
    params: {
      page: 1,
      limit: 100,
      assess_status: "Active"
    }
  });
}

export const getAdminListAssessment = async (): Promise<any> => {
  const authToken = getCookie("authToken");
  return await axios({
    url: `${apiURL}/assessments/list`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${authToken}`
    },
  });
}

export const getAssessment = async (assessmentId: string): Promise<Assessment> => {
  try {
    const authToken = getCookie("authToken") || getCookie("tempAuthToken");

    // if (!authToken) {
    //   return {} as Assessment; // Return an empty object if there's no auth token
    // }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/assessments/${assessmentId}`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`
      },
      timeout: 10000,
    });

    return response.data.data;

  } catch (error) {
    const err = error as AxiosError;

    return {} as Assessment; // Return an empty object if there's an error
  }
};

export const getUserInterviewSubmission = async (assessmentId: string, userId: string): Promise<UserInterview> => {
  try {
    const authToken = getCookie("authToken") || getCookie("tempAuthToken");

    if (!authToken) {
      return {} as UserInterview; // Return an empty array if there's no auth token
    }

    // Build query parameters if they exist
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId);
    params.append('submission_type', 'Interview'); // filter by interview submissions

    // Construct the URL with query parameters if they exist
    const queryString = params.toString();
    const url = `${apiURL}/assessments/${assessmentId}/user/submission${queryString ? `?${queryString}` : ''}`;

    const response: AxiosResponse = await axios({
      url,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`
      },
      timeout: 10000,
    });
    return {
      id: response.data.data[0]?.id || "",
      // AssessmentSubmission.id IS the conversation_id (v1 & v2). Prefer it; fall
      // back to the legacy answers-embedded value only if id is missing — and
      // guard the access, since v2 submissions may have no `answers`.
      conversation_id: response.data.data[0]?.id
        || response.data.data[0]?.answers?.answers?.find((answer: any) => answer.id === "conversation_id")?.content
        || "",
      assessment_id: response.data.data[0]?.assessment_id || "",
      jobseeker_id: response.data.data[0]?.jobseeker_id || "",
      updated_at: response.data.data[0]?.updated_at || "",
      status: response.data.data[0]?.status,
      submission_type: response.data.data[0]?.submission_type || "Interview",
    };

  } catch (error) {
    const err = error as AxiosError;

    return {} as UserInterview;
  }
};

export const getUserFormSubmission = async (assessmentId: string, userId: string): Promise<UserForm> => {
  try {
    const authToken = getCookie("authToken") || getCookie("tempAuthToken");

    if (!authToken) {
      return {} as UserForm; // Return an empty array if there's no auth token
    }

    // Build query parameters if they exist
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId);
    params.append('submission_type', "Form");

    // Construct the URL with query parameters if they exist
    const queryString = params.toString();
    const url = `${apiURL}/assessments/${assessmentId}/user/submission${queryString ? `?${queryString}` : ''}`;

    const response: AxiosResponse = await axios({
      url,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`
      },
      timeout: 10000,
    });
    return {
      id: response.data.data[0]?.id || "",
      answers: response.data.data[0]?.answers.answers,
      assessment_id: response.data.data[0]?.assessment_id || "",
      jobseeker_id: response.data.data[0]?.jobseeker_id || "",
      job_id: response.data.data[0]?.job_id || "",
      updated_at: response.data.data[0]?.updated_at || "",
      status: response.data.data[0]?.status || "Pending Interview",
      submission_type: response.data.data[0]?.submission_type || "Form",
    };

  } catch (error) {
    const err = error as AxiosError;

    return {} as UserForm;
  }
};

export const getAssessmentSubmissions = async (assessmentId: string): Promise<AssessmentSubmissions[]> => {
  try {
    const authToken = getCookie("authToken");

    if (!authToken) {
      return [] as AssessmentSubmissions[]; // Return an empty object if there's no auth token
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/assessments/${assessmentId}/submissions`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`
      },
      timeout: 10000,
    });

    return response.data.data;

  } catch (error) {
    const err = error as AxiosError;

    return [] as AssessmentSubmissions[];
  }
};

export const createAssessment = async (assessment: Assessment): Promise<ApiResponse<AssessmentResponse>> => {
  try {
    const authToken = getCookie("authToken");

    if (!authToken) {
      return {
        success: false,
        message: "Authentication required",
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/assessments/create`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`
      },
      data: assessment,
      timeout: 15000,
    });

    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    // This resolves with { success: false } instead of rejecting, so every
    // caller's `.catch()` is dead code — several callers discard the result
    // entirely and advance as if it worked. Reporting here is the only thing
    // that survives that, whatever the caller does.
    fatalErrorLog(
      "[assessment] create failed — the screening definition was not saved",
      {
        ...{ },
        http_status: err.response?.status ?? "none (network or timeout)",
        detail: (responseData?.message as string) || err.message,
      },
      { area: "assessment" },
    );

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED" ? "Request timed out" : "Failed to create screening"),
      error: err,
    };
  }
};

export const updateAssessment = async (assessmentId: string, updates: Assessment): Promise<ApiResponse<Assessment>> => {
  try {
    const authToken = getCookie("authToken");

    if (!authToken) {
      return {
        success: false,
        message: "Authentication required",
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/assessments/${assessmentId}`,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`
      },
      data: updates,
      timeout: 15000,
    });

    return {
      success: true,
      data: response.data.data,
      status: response.status,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    // This resolves with { success: false } instead of rejecting, so every
    // caller's `.catch()` is dead code — several callers discard the result
    // entirely and advance as if it worked. Reporting here is the only thing
    // that survives that, whatever the caller does.
    fatalErrorLog(
      "[assessment] update failed — the screening edits were not saved",
      {
        ...{ assessment_id: assessmentId },
        http_status: err.response?.status ?? "none (network or timeout)",
        detail: (responseData?.message as string) || err.message,
      },
      { area: "assessment" },
    );

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED" ? "Request timed out" : `Failed to update screening ${assessmentId}`),
      error: err,
    };
  }
};

export const deleteAssessment = async (assessmentId: string): Promise<ApiResponse<void>> => {
  try {
    const authToken = getCookie("authToken");

    if (!authToken) {
      return {
        success: false,
        message: "Authentication required",
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/assessments/${assessmentId}`,
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`
      },
      timeout: 10000,
    });

    return {
      success: true,
      status: response.status,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED" ? "Request timed out" : `Failed to delete screening ${assessmentId}`),
      error: err,
    };
  }
};

export const submitAssessment = async (submissionData: AssessmentSubmission): Promise<ApiResponse<void>> => {
  try {
    const authToken = getCookie("authToken") || getCookie("tempAuthToken");

    if (!authToken) {
      return {
        success: false,
        message: "Authentication required",
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/assessments/submit`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`
      },
      data: submissionData,
    });

    return {
      success: true,
      data: response.data.data,
      status: response.status,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    // This resolves with { success: false } instead of rejecting, so every
    // caller's `.catch()` is dead code — several callers discard the result
    // entirely and advance as if it worked. Reporting here is the only thing
    // that survives that, whatever the caller does.
    fatalErrorLog(
      "[assessment] submit failed — the candidate's answers were not recorded",
      {
        ...{ assessment_id: submissionData.assessment_id, job_id: submissionData.job_id, submission_type: submissionData.submission_type },
        http_status: err.response?.status ?? "none (network or timeout)",
        detail: (responseData?.message as string) || err.message,
      },
      { area: "assessment" },
    );

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED" ? "Request timed out" : `Failed to submit your responses (${submissionData.assessment_id})`),
      error: err,
    };
  }
}

export const submitAssessmentV2 = async (submissionData: AssessmentSubmission): Promise<ApiResponse<void>> => {
  try {
    const authToken = getCookie("authToken") || getCookie("tempAuthToken");

    if (!authToken) {
      return {
        success: false,
        message: "Authentication required",
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/assessments/v2/submit`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`
      },
      data: submissionData,
    });

    return {
      success: true,
      data: response.data.data,
      status: response.status,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    // Same dead-`.catch()` problem as v1 above, and the loss is the worst in
    // the product: the candidate has just finished a full AI interview and is
    // shown the completion screen regardless of whether this row was written.
    fatalErrorLog(
      "[assessment] submit failed — the candidate's answers were not recorded",
      {
        engine: "v2",
        assessment_id: submissionData.assessment_id,
        job_id: submissionData.job_id,
        submission_type: submissionData.submission_type,
        http_status: err.response?.status ?? "none (network or timeout)",
        detail: (responseData?.message as string) || err.message,
      },
      { area: "assessment" },
    );

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED" ? "Request timed out" : `Failed to submit your responses (${submissionData.assessment_id})`),
      error: err,
    };
  }
}

export const getPracticeAssessments = async (): Promise<InterviewAssessment[]> => {
  try {
    const authToken = getCookie("authToken");

    if (!authToken) {
      return [];
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/assessments/practice_interviews`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`
      },
      timeout: 15000,
    });

    return response.data.data as InterviewAssessment[];
  } catch (error) {
    console.error("Error fetching practice assessments:", error);
    return [];
  }
};

export const triggerDemoNotification = async (payload: AssessmentTriggerDemoNotifactionRequest): Promise<any> => {
  return await axios({
    url: `${apiURL}/assessments/trigger-demo-notification`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    data: payload,
  });
};


export const getAssessmentCompanyDemo = async (companyName: string): Promise<any> => {
  return await axios({
    url: `${apiURL}/assessments/demo-company-info`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    params: {
      alias: companyName
    }
  });
};

export const getAssessmentDetail = async (assessment_id: string): Promise<any> => {
  const authToken = getCookie("authToken");
  return await axios({
    url: `${apiURL}/assessments/${assessment_id}`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${authToken}`
    },
  });
}

export const getMyAssessments = async (params: GetMyAssessmentParams): Promise<any> => {
  const authToken = getCookie("authToken");
  return await axios({
    url: `${apiURL}/assessments/hr/my_assessments`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${authToken}`
    },
    params
  })
};

export const getJobAssessmentSubmission = async (params: GetSubmissionJobAssessmentParams): Promise<any> => {
  const authToken = getCookie("authToken");
  return await axios({
    url: `${apiURL}/assessments/${params.assessment_id}/jobs/${params.job_id}/submissions`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${authToken}`
    },
    params,
  })
};

export const createUpdateDemoInterview = async (body: DemoInterviewFormRequest): Promise<any> => {
  const form = new FormData();
  form.append("company_name", body.company_name);
  form.append("company_color", body.company_color);
  if (body.company_logo_interview) {
    form.append("company_logo_interview", body.company_logo_interview);
  }
  if (body.company_logo) {
    form.append("company_logo", body.company_logo);
  }
  form.append("alias", body.alias);
  if (body.assessment_id) {
    form.append("assessment_id", body.assessment_id);
  }

  const authToken = getCookie("authToken");
  return await axios({
    url: `${apiURL}/assessments/demo-company-info`,
    method: "POST",
    headers: {
      "Authorization": `Bearer ${authToken}`
    },
    data: form
  });
}

export const getDemoCompanyFilter = async (params: GetDemoCompanyFilterParams): Promise<any> => {
  const authToken = getCookie("authToken");
  return await axios({
    url: `${apiURL}/assessments/all-demo-company`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${authToken}`
    },
    params
  });
}

export const getDemoCompanyDetail = async (alias: string): Promise<any> => {
  const authToken = getCookie("authToken");
  return await axios({
    url: `${apiURL}/assessments/demo-company-info`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${authToken}`
    },
    params: {
      alias
    }
  });
}

export const demoInterviewInsight = (conversation_id: string): Promise<any> => {
  return axios({
    url: `${apiURL}/interviews/demo-interview-insights`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    params: {
      conversation_id
    },
  });
}

export const updateStatusAssessment = async (assessment_id: string, status: string): Promise<any> => {
  const authToken = getCookie("authToken");
  return await axios({
    url: `${apiURL}/assessments/${assessment_id}/update-status`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${authToken}`
    },
    data: {
      status
    },
  })
};

export const duplicateAssessment = async (assessmentId: string): Promise<ApiResponse<AssessmentResponse>> => {
  try {
    const authToken = getCookie("authToken");

    if (!authToken) {
      return {
        success: false,
        message: "Authentication required",
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/assessments/${assessmentId}/duplicate`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`
      },
      timeout: 15000,
    });

    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED" ? "Request timed out" : `Failed to duplicate screening ${assessmentId}`),
      error: err,
    };
  }
};

export const generateInterviewSections = async (body: GenerateInterviewSectionRequest): Promise<any> => {
  const authToken = getCookie("authToken");
  return await axios({
    url: `${apiURL}/assessments/generate-interview-sections`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${authToken}`
    },
    data: body,
  })
};

export const getAssessmentInfo = async (body: AssessmentInfoRequest): Promise<any> => {
  return await axios({
    url: `${apiURL}/assessments/assessment-info`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    data: body,
  });
};

export const recordFormAssessmentStarted = async (job_id: string): Promise<AxiosResponse> => {
  const authToken = getCookie("authToken") || getCookie("tempAuthToken");
  return await axios({
    url: `${apiURL}/assessments/form-started`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${authToken}`
    },
    data: { job_id },
  });
};

export const recordEligibilityCheckStarted = async (job_id: string): Promise<AxiosResponse> => {
  const authToken = getCookie("authToken") || getCookie("tempAuthToken");
  return await axios({
    url: `${apiURL}/assessments/eligibility-started`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${authToken}`
    },
    data: { job_id },
  });
};

export const recordAIInterviewStarted = async (job_id: string): Promise<AxiosResponse> => {
  const authToken = getCookie("authToken") || getCookie("tempAuthToken");
  return await axios({
    url: `${apiURL}/assessments/ai-interview-started`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${authToken}`
    },
    data: { job_id },
  });
};

export const sendDropOffReminder = async (applicationId: string): Promise<ApiResponse<void>> => {
  try {
    const authToken = getCookie("authToken");

    if (!authToken) {
      return {
        success: false,
        message: "Authentication required",
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/assessments/drop-off-reminder`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authToken}`
      },
      data: {
        application_id: applicationId,
      },
    });

    return {
      success: true,
      data: response.data.data,
      status: response.status,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED" ? "Request timed out" : "Failed to send drop-off reminder"),
      error: err,
    };
  }
};
/**
 * A question of a Form / Questionnaire assessment, for the flow builder's
 * knockout `question_ref` dropdown (#3195, BE ourteam-backend#3883).
 */
export interface AssessmentQuestionOption {
  /** The question id used as `hiring_flow.steps[].gate.question_ref`. */
  id: string;
  /** Human-readable question text (HTML-stripped by the BE). */
  label: string;
  /** e.g. "Multiple Choice" | "Rating Scale" | "Short Answer". */
  type?: string;
  /** Multiple-choice options — lets the builder offer them for the gate value. */
  options?: string[];
  min_rate?: number;
  max_rate?: number;
}

/**
 * List a Form assessment's questions for the flow-builder knockout picker.
 *
 * Contract (BE ourteam-backend#3883 — NOT yet released; the builder falls back to
 * a free-text `question_ref` input until this is live, so it's safe ahead of BE):
 *   GET /assessments/{assessment_id}/questions   (HR auth)
 *     → { code, data: [{ id, label, type, options?, min_rate?, max_rate? }] }  (in order)
 * Returns [] on any failure/absence so the caller degrades to the textbox. Field
 * names come straight from the BE ticket — none invented.
 */
export const getAssessmentQuestions = async (assessmentId: string): Promise<AssessmentQuestionOption[]> => {
  const authToken = getCookie("authToken") || getCookie("tempAuthToken");
  if (!assessmentId || !authToken) return [];
  try {
    const res: AxiosResponse = await axios.get(`${apiURL}/assessments/${assessmentId}/questions`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      timeout: 10000,
    });
    const data = res.data?.data;
    return Array.isArray(data) ? (data as AssessmentQuestionOption[]) : [];
  } catch {
    // Endpoint not live yet / no access — caller falls back to the free-text ref.
    return [];
  }
};
